import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connectSocket, onSocket } from "../../services/socketService";
import { PhoneIncoming, X, ExternalLink, User, Mail, Globe, ShieldCheck } from "lucide-react";

// Listens globally for "incoming_transfer" events (an AI campaign call being
// warm-transferred to this agent) and shows a screen-pop with the customer's
// context, KYC and the live transcript. Mounted once from Layout.jsx.
export function IncomingTransferPopup() {
    const [pops, setPops] = useState([]); // most recent last
    const navigate = useNavigate();

    useEffect(() => {
        connectSocket();
        const off = onSocket("incoming_transfer", (data) => {
            if (!data) return;
            setPops((prev) => [...prev.filter((p) => p.retellCallId !== data.retellCallId), { ...data, _key: `${data.retellCallId || ""}-${Date.now()}` }]);
        });
        return () => off?.();
    }, []);

    const dismiss = (key) => setPops((prev) => prev.filter((p) => p._key !== key));

    const openLead = (p) => {
        dismiss(p._key);
        if (p.leadId) navigate(`/leads/edit/${p.leadId}`);
    };

    if (pops.length === 0) return null;

    // Show the most recent pop as a prominent centered modal.
    const p = pops[pops.length - 1];
    const kyc = p.kyc || null;

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border-2 border-emerald-400 overflow-hidden animate-in zoom-in-95">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-3 flex items-center gap-2 text-white">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
                    </span>
                    <PhoneIncoming className="h-5 w-5" />
                    <span className="text-sm font-bold uppercase tracking-wide">Incoming transfer</span>
                    {p.campaignName && <span className="ml-2 text-[11px] opacity-80 truncate">· {p.campaignName}</span>}
                    <button onClick={() => dismiss(p._key)} className="ml-auto p-1 rounded hover:bg-white/20" aria-label="Dismiss">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-5 py-4">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <p className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <User className="h-5 w-5 text-gray-400" />
                                {p.name || "Unknown caller"}
                            </p>
                            {p.phone && <p className="text-sm text-gray-600 mt-0.5 font-mono">{p.phone}</p>}
                        </div>
                        {p.status && (
                            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-700 whitespace-nowrap">
                                {p.status}
                            </span>
                        )}
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                        {p.email && (
                            <p className="text-gray-700 flex items-center gap-1.5 truncate">
                                <Mail className="h-3.5 w-3.5 text-gray-400 shrink-0" /> <span className="truncate">{p.email}</span>
                            </p>
                        )}
                        {p.country && (
                            <p className="text-gray-700 flex items-center gap-1.5">
                                <Globe className="h-3.5 w-3.5 text-gray-400 shrink-0" /> {p.country}
                            </p>
                        )}
                    </div>

                    {kyc && (kyc.summary || kyc.amountLost || kyc.whenHappened || kyc.age || kyc.fraudRelated != null || kyc.hasEvidence != null) && (
                        <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1.5 mb-1">
                                <ShieldCheck className="h-3.5 w-3.5" /> Pre-call KYC
                            </p>
                            <div className="text-sm text-gray-800 space-y-0.5">
                                {kyc.fraudRelated != null && (
                                    <p><span className="text-gray-500">Fraud/scam related:</span> {kyc.fraudRelated ? "Yes" : "No"}</p>
                                )}
                                {kyc.amountLost && <p><span className="text-gray-500">Amount affected:</span> {kyc.amountLost}</p>}
                                {(kyc.platformNames || kyc.remembersPlatforms !== null) && (
                                    <p>
                                        <span className="text-gray-500">Remembers platforms:</span>{" "}
                                        {kyc.remembersPlatforms === false ? "No" : "Yes"}
                                        {kyc.platformNames ? ` (${kyc.platformNames})` : ""}
                                    </p>
                                )}
                                {kyc.whenHappened && <p><span className="text-gray-500">When:</span> {kyc.whenHappened}</p>}
                                {kyc.age && <p><span className="text-gray-500">Age:</span> {kyc.age}</p>}
                                {kyc.hasEvidence != null && (
                                    <p>
                                        <span className="text-gray-500">Evidence:</span>{" "}
                                        {kyc.hasEvidence ? "Yes" : "No"}
                                        {kyc.evidenceNotes ? ` (${kyc.evidenceNotes})` : ""}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {p.transcript && (
                        <div className="mt-3">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Conversation so far</p>
                            <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {p.transcript}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 mt-4">
                        <button
                            onClick={() => openLead(p)}
                            className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-1.5"
                        >
                            <ExternalLink className="h-4 w-4" />
                            Open lead
                        </button>
                        <button
                            onClick={() => dismiss(p._key)}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
